import React, { useContext, useEffect, useMemo, useRef, useState } from "react";

import { ZeosApiContext } from "../../context/ZeosApiContext";
import { IUtilityProfileState, useUtilityProfile } from "../../hooks/useUtilityProfile";
import { IPropertyFormDataType } from "../../services/forms/FormDataTypes";
import { IListItemSimple } from "../../services/zeos-api/contracts/ListUtility";
import { IUtilityCompanyResponse } from "../../services/zeos-api/contracts/UtilityCompany";
import {
  IServiceResolutionResult,
  UtilityAssignmentSource,
  UtilityResolutionStatus,
} from "../../services/zeos-api/contracts/UtilityIntelligence";
import { ZeosLabel } from "../Shared/ZeosLabel";
import { ZeosSimpleList } from "../Shared/ZeosSimpleList/ZeosSimpleList";

// 3898 Q2 (UTL P03, hybrid per Al 27 Aug on 3945):
//  - AMBIGUOUS: the list is the lookup's candidate set plus "Not sure". "Not sure" saves the service
//    blank and the question stays open on the property; the API accepts a blank only here.
//  - RESOLVED: pre-filled and still changeable from the state list; a change is recorded as the
//    owner's choice and reviewed against the territory data.
//  - Anything else (unresolved, lookup failed): the state list, as before.

interface IProps {
  formData: IPropertyFormDataType;
  errors: Record<string, string>;
  setFormData: React.Dispatch<React.SetStateAction<IPropertyFormDataType>>;
  disabled?: boolean;
  /** The stored profile from PropertyForm, shared with PropertyUtilityPanel. Omitted, this loads its own. */
  utilityProfile?: IUtilityProfileState;
}

const NO_PIPED_GAS: IListItemSimple = {
  dataKey: -1,
  display: "No piped gas",
  value: "None",
};

export const NOT_SURE_VALUE = "__NOT_SURE__";

const NOT_SURE: IListItemSimple = {
  dataKey: -2,
  display: "Not sure",
  value: NOT_SURE_VALUE,
};

const candidateItems = (service?: IServiceResolutionResult): IListItemSimple[] =>
  (service?.candidates ?? [])
    .filter((c) => (c.utilityName ?? "").trim() !== "")
    .map((c) => ({ dataKey: c.utilityId, display: c.utilityName, value: c.utilityName }));

const isAmbiguous = (service?: IServiceResolutionResult) =>
  service?.status === UtilityResolutionStatus.Ambiguous && candidateItems(service).length > 0;

const isElectricType = (typeName?: string | null) =>
  /electric/i.test(typeName ?? "");

const isGasType = (typeName?: string | null) => /gas/i.test(typeName ?? "");

const toListItem = (company: IUtilityCompanyResponse): IListItemSimple => ({
  dataKey: company.utilityId,
  display: company.name,
  value: company.name,
});

const ensureOption = (
  items: IListItemSimple[],
  name?: string
): IListItemSimple[] => {
  const trimmed = name?.trim();
  if (!trimmed) {
    return items;
  }
  if (items.some((item) => item.value === trimmed)) {
    return items;
  }
  return [{ dataKey: 0, display: trimmed, value: trimmed }, ...items];
};

export const ServingUtilityFields: React.FC<IProps> = ({
  formData,
  errors,
  setFormData,
  disabled = false,
  utilityProfile,
}) => {
  const api = useContext(ZeosApiContext);
  const [companies, setCompanies] = useState<IUtilityCompanyResponse[]>([]);
  const [electricService, setElectricService] = useState<IServiceResolutionResult | undefined>();
  const [gasService, setGasService] = useState<IServiceResolutionResult | undefined>();
  const lastProposalRef = useRef<{ electric: string; gas: string }>({
    electric: "",
    gas: "",
  });
  const requestIdRef = useRef(0);

  useEffect(() => {
    const stateCode = formData.state?.trim();
    if (!stateCode) {
      setCompanies([]);
      return;
    }

    let cancelled = false;
    const load = async () => {
      try {
        const response = await api.Utility.getCompaniesAsync(stateCode);
        if (!cancelled) {
          setCompanies(response.data ?? []);
        }
      } catch {
        if (!cancelled) {
          setCompanies([]);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [api.Utility, formData.state]);

  useEffect(() => {
    const zip = formData.zip?.trim();
    if (!zip || zip.length < 5) {
      return;
    }

    const requestId = ++requestIdRef.current;
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const response = await api.Territory.proposeProfileAsync(
            formData.address,
            formData.city,
            formData.state,
            zip
          );
          if (requestId !== requestIdRef.current) {
            return;
          }

          const electric = response.data?.services.find(s => s.serviceCode === "electric");
          const gas = response.data?.services.find(s => s.serviceCode === "natural_gas");
          setElectricService(electric);
          setGasService(gas);

          const proposedElectric = (electric?.status === UtilityResolutionStatus.Resolved
            ? electric.utilityName ?? ""
            : "").trim();
          const proposedGas = (gas?.status === UtilityResolutionStatus.Resolved
            ? gas.utilityName ?? ""
            : "").trim();

          setFormData((prev) => {
            const currentElectric = (prev.electricUtilityProvider ?? "").trim();
            const currentGas = (prev.gasUtilityProvider ?? "").trim();
            const last = lastProposalRef.current;

            const nextElectric =
              !currentElectric || currentElectric === last.electric
                ? proposedElectric
                : currentElectric;
            const nextGas =
              !currentGas || currentGas === last.gas
                ? proposedGas
                : currentGas;
            // "Not sure" only stands while the lookup is still AMBIGUOUS for that service.
            const nextElectricNotSure = Boolean(prev.electricUtilityNotSure) && isAmbiguous(electric);
            const nextGasNotSure = Boolean(prev.gasUtilityNotSure) && isAmbiguous(gas);

            lastProposalRef.current = {
              electric: proposedElectric,
              gas: proposedGas,
            };

            if (
              nextElectric === currentElectric &&
              nextGas === currentGas &&
              nextElectricNotSure === Boolean(prev.electricUtilityNotSure) &&
              nextGasNotSure === Boolean(prev.gasUtilityNotSure)
            ) {
              return prev;
            }
            return {
              ...prev,
              electricUtilityProvider: nextElectric,
              gasUtilityProvider: nextGas,
              electricUtilityNotSure: nextElectricNotSure,
              gasUtilityNotSure: nextGasNotSure,
            };
          });
        } catch {
          // User can still pick from the type-ahead if the ZIP table misses.
        }
      })();
    }, 250);

    return () => {
      window.clearTimeout(timer);
    };
  }, [
    api.Territory,
    formData.address,
    formData.city,
    formData.state,
    formData.zip,
    setFormData,
  ]);

  // A saved property's stored answer: a utility a person already confirmed (user_selected /
  // admin_override) closes the AMBIGUOUS question even though a fresh lookup is still ambiguous.
  const propertyId = Number(formData.propertyId ?? 0);
  const ownProfile = useUtilityProfile(propertyId, !utilityProfile);
  const storedProfile = (utilityProfile ?? ownProfile).profile;
  const storedServices = useMemo(() => storedProfile?.services ?? [], [storedProfile]);

  // A "Not sure" saved earlier comes back as a blank provider on an AMBIGUOUS service nobody has
  // confirmed. Restore the choice once per property so a later toggle by the person stands.
  const notSureRestoredForRef = useRef(0);
  useEffect(() => {
    if (propertyId <= 0 || !storedProfile || notSureRestoredForRef.current === propertyId) {
      return;
    }
    notSureRestoredForRef.current = propertyId;
    const storedNotSure = (serviceCode: string) =>
      storedServices.some(
        (s) =>
          s.serviceCode === serviceCode &&
          s.status === UtilityResolutionStatus.Ambiguous &&
          (!s.assignmentSource || s.assignmentSource === UtilityAssignmentSource.System)
      );
    const electricNotSure = storedNotSure("electric");
    const gasNotSure = storedNotSure("natural_gas");
    if (!electricNotSure && !gasNotSure) return;
    setFormData((prev) => {
      const restoreElectric =
        electricNotSure && !(prev.electricUtilityProvider ?? "").trim() && !prev.electricUtilityNotSure;
      const restoreGas = gasNotSure && !(prev.gasUtilityProvider ?? "").trim() && !prev.gasUtilityNotSure;
      if (!restoreElectric && !restoreGas) return prev;
      return {
        ...prev,
        electricUtilityNotSure: restoreElectric ? true : prev.electricUtilityNotSure,
        gasUtilityNotSure: restoreGas ? true : prev.gasUtilityNotSure,
      };
    });
  }, [propertyId, setFormData, storedProfile, storedServices]);

  const confirmedByPerson = (serviceCode: string, value?: string) =>
    storedServices.some(
      (s) =>
        s.serviceCode === serviceCode &&
        s.status === UtilityResolutionStatus.Resolved &&
        s.assignmentSource !== undefined &&
        s.assignmentSource !== null &&
        s.assignmentSource !== UtilityAssignmentSource.System &&
        (s.utilityName ?? "").trim() !== "" &&
        (s.utilityName ?? "").trim() === (value ?? "").trim()
    );

  const electricAmbiguous =
    isAmbiguous(electricService) && !confirmedByPerson("electric", formData.electricUtilityProvider);
  const gasAmbiguous =
    isAmbiguous(gasService) && !confirmedByPerson("natural_gas", formData.gasUtilityProvider);

  // An AMBIGUOUS service already holding one of its candidates still needs a save to confirm it.
  const isPendingCandidate = (service: IServiceResolutionResult | undefined, value?: string, notSure?: boolean) =>
    isAmbiguous(service) &&
    !confirmedByPerson(service?.serviceCode ?? "", value) &&
    !notSure &&
    candidateItems(service).some((item) => item.value === (value ?? "").trim());
  const confirmationPending =
    isPendingCandidate(electricService, formData.electricUtilityProvider, formData.electricUtilityNotSure) ||
    isPendingCandidate(gasService, formData.gasUtilityProvider, formData.gasUtilityNotSure);

  useEffect(() => {
    if (Boolean(formData.servingUtilityConfirmationPending) !== confirmationPending) {
      setFormData((prev) => ({ ...prev, servingUtilityConfirmationPending: confirmationPending }));
    }
  }, [confirmationPending, formData.servingUtilityConfirmationPending, setFormData]);

  const electricItems = useMemo(
    () =>
      electricAmbiguous
        ? [...ensureOption(candidateItems(electricService), formData.electricUtilityProvider), NOT_SURE]
        : ensureOption(
            companies.filter((c) => isElectricType(c.utilityTypeName)).map(toListItem),
            formData.electricUtilityProvider
          ),
    [companies, electricAmbiguous, electricService, formData.electricUtilityProvider]
  );

  const gasItems = useMemo(
    () =>
      gasAmbiguous
        ? [NO_PIPED_GAS, ...ensureOption(candidateItems(gasService), formData.gasUtilityProvider), NOT_SURE]
        : ensureOption(
            [
              NO_PIPED_GAS,
              ...companies.filter((c) => isGasType(c.utilityTypeName)).map(toListItem),
            ],
            formData.gasUtilityProvider
          ),
    [companies, gasAmbiguous, gasService, formData.gasUtilityProvider]
  );

  const selectedElectric = formData.electricUtilityNotSure
    ? NOT_SURE
    : electricItems.find((item) => item.value === (formData.electricUtilityProvider ?? "").trim());
  const selectedGas = formData.gasUtilityNotSure
    ? NOT_SURE
    : gasItems.find((item) => item.value === (formData.gasUtilityProvider ?? "").trim());

  // A RESOLVED pre-fill the owner replaced: saved as their choice and reviewed (3898 Q2).
  const changedFromResolved = (service: IServiceResolutionResult | undefined, value?: string) =>
    service?.status === UtilityResolutionStatus.Resolved &&
    (value ?? "").trim() !== "" &&
    (value ?? "").trim() !== "None" &&
    (value ?? "").trim() !== (service.utilityName ?? "").trim();

  const choose = (service: "electric" | "gas", item?: IListItemSimple | null) => {
    const notSure = item?.value === NOT_SURE_VALUE;
    setFormData((prev) =>
      service === "electric"
        ? { ...prev, electricUtilityProvider: notSure ? "" : item?.value ?? "", electricUtilityNotSure: notSure }
        : { ...prev, gasUtilityProvider: notSure ? "" : item?.value ?? "", gasUtilityNotSure: notSure }
    );
  };

  return (
    <>
      <div className="col-span-6 px-2">
        <ZeosLabel label="Electric utility" required />
        <ZeosSimpleList
          key={`electric-${electricItems.map((i) => i.value).join("|")}-${selectedElectric?.value ?? ""}`}
          listDefinition={electricItems}
          defaultValue={selectedElectric}
          onChange={(item) => choose("electric", item)}
          placeholder={
            electricAmbiguous
              ? "Choose the electric utility on your bill"
              : electricItems.length > 0
                ? "Confirm or choose electric utility"
                : "Enter state to load utilities"
          }
          searchable
          clearable
          disabled={disabled}
        />
        {electricAmbiguous && (
          <p className="text-zeos-orange-dk text-sm mt-1">
            More than one electric utility serves this area. Choose the one on your bill, or Not sure.
          </p>
        )}
        {changedFromResolved(electricService, formData.electricUtilityProvider) && (
          <p className="text-zeos-black/70 text-sm mt-1">
            We found {electricService?.utilityName} for this address. We&apos;ll review your choice.
          </p>
        )}
        {errors.electricUtilityProvider ? (
          <p className="text-zeos-orange-dk text-sm mt-1">
            {errors.electricUtilityProvider}
          </p>
        ) : null}
      </div>
      <div className="col-span-6 px-2">
        <ZeosLabel label="Gas utility" required />
        <ZeosSimpleList
          key={`gas-${gasItems.map((i) => i.value).join("|")}-${selectedGas?.value ?? ""}`}
          listDefinition={gasItems}
          defaultValue={selectedGas}
          onChange={(item) => choose("gas", item)}
          placeholder={
            gasAmbiguous
              ? "Choose the gas utility on your bill, or No piped gas"
              : gasItems.length > 0
                ? "Confirm gas utility or No piped gas"
                : "Enter state to load utilities"
          }
          searchable
          clearable
          disabled={disabled}
        />
        {gasAmbiguous && (
          <p className="text-zeos-orange-dk text-sm mt-1">
            More than one gas utility serves this area. Choose the one on your bill, No piped gas, or Not sure.
          </p>
        )}
        {changedFromResolved(gasService, formData.gasUtilityProvider) && (
          <p className="text-zeos-black/70 text-sm mt-1">
            We found {gasService?.utilityName} for this address. We&apos;ll review your choice.
          </p>
        )}
        {errors.gasUtilityProvider ? (
          <p className="text-zeos-orange-dk text-sm mt-1">
            {errors.gasUtilityProvider}
          </p>
        ) : null}
      </div>
      <div className="col-span-12 px-2 -mt-2 mb-2">
        <p className="text-sm text-zeos-black/70">
          We use a resolution engine to determine the serving utilities for this property.
          If more than one utility serves the area, choose the one on your bill, or Not sure
          and you can confirm it later from the property.
          Electric is required. For gas, choose a provider or select
          &quot;No piped gas&quot; if the building has none.
        </p>
      </div>
    </>
  );
};
